import React from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faThumbtack } from "@fortawesome/pro-regular-svg-icons";

import { IMergedReportEntry } from "pages/secure/shared/reports/reportCatalog.types";
import { Chip } from "components/Chip/Chip";
import { getReportLockedTooltipId, useReportLockedTooltipControl } from "./ReportLockedTooltipContext";

interface IReportsPinnedBarProps {
  pinnedReports: IMergedReportEntry[];
  onSelectReport: (report: IMergedReportEntry) => void;
}

interface ILockedPinnedChipProps {
  lockedTooltipId: string;
  lockedTooltipMessage: string;
  label: string;
}

function LockedPinnedChip(props: ILockedPinnedChipProps) {
  const { lockedTooltipId, lockedTooltipMessage, label } = props;
  const { show, lockedTooltipTrigger, onToggle } = useReportLockedTooltipControl(lockedTooltipId);

  return (
    <li>
      <span className="ui-chip ui-chip--md reports-list-pinned__locked-chip" aria-disabled="true">
        <OverlayTrigger
          placement="top"
          trigger={lockedTooltipTrigger}
          show={show}
          onToggle={onToggle}
          overlay={<Tooltip id={lockedTooltipId}>{lockedTooltipMessage}</Tooltip>}
        >
          <button
            type="button"
            className="reports-list-pinned__locked-icon-btn"
            aria-label={lockedTooltipMessage}
          >
            <FontAwesomeIcon icon={faLock} aria-hidden="true" />
          </button>
        </OverlayTrigger>
        <span className="ui-chip__label">{label}</span>
      </span>
    </li>
  );
}

export default function ReportsPinnedBar(props: IReportsPinnedBarProps) {
  const { pinnedReports, onSelectReport } = props;
  const { t } = useTranslation();

  if (pinnedReports.length === 0) {
    return null;
  }

  return (
    <div className="reports-list-pinned" aria-labelledby="reports-list-pinned-label">
      <span id="reports-list-pinned-label" className="reports-list-pinned__label">
        {t("Pinned")}
      </span>
      <span className="reports-list-pinned__divider" aria-hidden="true" />
      <ul className="reports-list-pinned__chips">
        {pinnedReports.map(report => {
          if (!report.hasPermission) {
            const lockedTooltipMessage = t("You do not have access to view this report.");
            const lockedTooltipId = getReportLockedTooltipId(report.code, "pinned");

            return (
              <LockedPinnedChip
                key={report.code}
                lockedTooltipId={lockedTooltipId}
                lockedTooltipMessage={lockedTooltipMessage}
                label={t(report.title)}
              />
            );
          }

          return (
            <li key={report.code}>
              <Chip
                size="md"
                label={t(report.title)}
                icon={<FontAwesomeIcon icon={faThumbtack} />}
                onClick={() => onSelectReport(report)}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
